import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Download, Shield, Sparkles, Check } from 'lucide-react';

const DigitalDownload = () => {
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [email, setEmail] = useState('');
  const [showPayment, setShowPayment] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    
    setLoading(true);
    try {
      // Track order intent
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email,
          product_type: 'digital_download',
          amount: 10.00,
          payment_method: 'pending'
        })
      });
      setShowPayment(true);
    } catch (err) {
      console.error('Order tracking failed:', err);
      setShowPayment(true); // Still show payment even if tracking fails
    } finally {
      setLoading(false);
    }
  };

  const handleCryptoPayment = async () => {
    // Track crypto payment intent
    await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/access/order/create`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        product_type: 'digital_download',
        amount: 10.00,
        payment_method: 'crypto'
      })
    });
    // Redirect to NOWPayments
    window.open('https://nowpayments.io/payment/?iid=5948713979', '_blank');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #1a0a2e 100%)',
      padding: '2rem',
      paddingTop: '6rem'
    }}>
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00ffff, #00ff88)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '1rem'
          }}>
            Digital Library Access
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255, 255, 255, 0.7)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            $10 per document • Instant access • Any document from the NextXus library
          </p>
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
          gap: '2rem',
          alignItems: 'start'
        }}>
          {/* Product Details */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)'
          }}>
            <div style={{ marginBottom: '2rem' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                marginBottom: '1rem'
              }}>
                <Sparkles size={24} color="#00ffff" />
                <h2 style={{
                  fontSize: '1.8rem',
                  fontWeight: 'bold',
                  color: '#00ffff'
                }}>
                  Digital Document Access
                </h2>
              </div>
              
              <div style={{
                fontSize: '3rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                $10.00
              </div>

              <p style={{
                color: 'rgba(255, 255, 255, 0.6)',
                fontSize: '0.9rem',
                marginBottom: '2rem'
              }}>
                One-time payment • Instant access • Any document
              </p>
            </div>

            {/* What's Included */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{
                fontSize: '1.2rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                Choose Any Document:
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {[
                  'NextXus Architecture & Vision Documents',
                  'The HumanCodex Consciousness Manual',
                  'Keyserling Emotion Equations (FME-24)',
                  'Ring of Six Framework & Implementation',
                  '200-Year Mandate: Legacy Architecture',
                  'Agent Zero Truth System Documentation',
                  'Federation Protocol & Integration Guides',
                  'Or any other document from the library'
                ].map((item) => (
                  <div key={item} style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}>
                    <Check size={18} color="#00ff88" />
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Features */}
            <div style={{
              background: 'rgba(0, 255, 255, 0.05)',
              border: '1px solid rgba(0, 255, 255, 0.2)',
              borderRadius: '8px',
              padding: '1rem',
              marginTop: '2rem'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Shield size={16} color="#00ffff" />
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                    Secure crypto payment (USDC/ETH)
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Download size={16} color="#00ffff" />
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                    Instant download after payment
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Sparkles size={16} color="#00ffff" />
                  <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                    Lifetime access & updates
                  </span>
                </div>
              </div>
            </div>
          </Card>

          {/* Payment Widget */}
          <Card style={{
            background: 'rgba(5, 5, 15, 0.95)',
            border: '2px solid rgba(0, 255, 255, 0.3)',
            padding: '2rem',
            boxShadow: '0 20px 60px rgba(0, 255, 255, 0.2)',
            position: 'sticky',
            top: '100px'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'white',
              marginBottom: '1rem',
              textAlign: 'center'
            }}>
              {showPayment ? 'Choose Payment Method' : 'Enter Your Email'}
            </h3>

            {!showPayment ? (
              // Email Collection Form
              <form onSubmit={handleEmailSubmit}>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  marginBottom: '1.5rem'
                }}>
                  We'll send your download link to this email
                </p>

                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    marginBottom: '1rem',
                    background: 'rgba(0, 0, 0, 0.5)',
                    border: '1px solid rgba(0, 255, 255, 0.3)',
                    borderRadius: '8px',
                    color: 'white',
                    fontSize: '1rem'
                  }}
                />

                <Button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                    color: '#000',
                    fontWeight: 'bold',
                    padding: '0.75rem',
                    fontSize: '1.1rem'
                  }}
                >
                  {loading ? 'Processing...' : 'Continue to Payment →'}
                </Button>
              </form>
            ) : (
              // Payment Options
              <div>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.9rem',
                  textAlign: 'center',
                  marginBottom: '1.5rem'
                }}>
                  Sending download link to: <strong style={{color: '#00ffff'}}>{email}</strong>
                </p>

                {/* Crypto Payment Button */}
                <div style={{
                  background: 'rgba(0, 255, 255, 0.05)',
                  border: '2px solid rgba(0, 255, 255, 0.3)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  marginBottom: '1rem',
                  textAlign: 'center'
                }}>
                  <h4 style={{
                    color: '#00ffff',
                    fontSize: '1.1rem',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold'
                  }}>
                    💎 Pay with Crypto
                  </h4>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.85rem',
                    marginBottom: '1rem'
                  }}>
                    USDC or ETH • Secure • Instant
                  </p>
                  <Button
                    onClick={handleCryptoPayment}
                    style={{
                      width: '100%',
                      background: 'linear-gradient(135deg, #00ffff, #00ff88)',
                      color: '#000',
                      fontWeight: 'bold',
                      padding: '1rem',
                      fontSize: '1.1rem',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Pay $10.00 with Crypto →
                  </Button>
                </div>

                {/* PayPal Payment */}
                <div style={{
                  background: 'rgba(255, 215, 0, 0.05)',
                  border: '2px solid rgba(255, 215, 0, 0.3)',
                  borderRadius: '12px',
                  padding: '1.5rem',
                  textAlign: 'center'
                }}>
                  <h4 style={{
                    color: '#FFD700',
                    fontSize: '1.1rem',
                    marginBottom: '0.5rem',
                    fontWeight: 'bold'
                  }}>
                    💳 Pay with PayPal
                  </h4>
                  <p style={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '0.85rem',
                    marginBottom: '1rem'
                  }}>
                    Credit Card • Debit • PayPal Balance
                  </p>
                  <div id="paypal-button-container-S6VDG3L6CBG8Y"></div>
                  <script>
                    {`
                      paypal.Buttons({
                        hostedButtonId: "S6VDG3L6CBG8Y",
                        style: {
                          color: 'gold',
                          shape: 'rect',
                          label: 'buy',
                          height: 45
                        }
                      }).render('#paypal-button-container-S6VDG3L6CBG8Y');
                    `}
                  </script>
                </div>

                <p style={{
                  textAlign: 'center',
                  fontSize: '0.75rem',
                  color: 'rgba(255, 255, 255, 0.5)',
                  marginTop: '1rem'
                }}>
                  🔒 Secure payment • Download link sent to {email}
                </p>
              </div>
            )}
          </Card>
        </div>

        {/* Download Section (shows after payment) */}
        {paymentComplete && (
          <Card style={{
            background: 'rgba(0, 255, 136, 0.1)',
            border: '2px solid rgba(0, 255, 136, 0.5)',
            padding: '2rem',
            marginTop: '2rem',
            textAlign: 'center'
          }}>
            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: '#00ff88',
              marginBottom: '1rem'
            }}>
              ✅ Payment Confirmed!
            </h3>
            <p style={{
              color: 'rgba(255, 255, 255, 0.9)',
              marginBottom: '1.5rem'
            }}>
              Your download is ready
            </p>
            <Button style={{
              background: 'linear-gradient(135deg, #00ff88, #00ffff)',
              color: '#000',
              padding: '1rem 2rem',
              fontSize: '1.1rem',
              fontWeight: 'bold'
            }}>
              <Download size={20} style={{ marginRight: '0.5rem' }} />
              Download Your Files
            </Button>
          </Card>
        )}

        {/* Trust Badges */}
        <div style={{
          marginTop: '3rem',
          textAlign: 'center',
          color: 'rgba(255, 255, 255, 0.6)',
          fontSize: '0.9rem'
        }}>
          <p>🔒 Secure crypto payment • 🌐 Decentralized • 💎 One-time purchase</p>
          <p style={{ marginTop: '0.5rem' }}>
            Questions? Contact: keywebco@gmail.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default DigitalDownload;
